/**
 * Feature Flags for the Editor
 *
 * These flags control experimental or in-development features.
 * Set to true to enable the feature, false to use the legacy behavior.
 */

/**
 * USE_SETUP_WIZARD_FLOW
 *
 * When ENABLED (true):
 *   - Hides the template selection in the New Project dialog
 *   - Automatically selects the 'empty' template
 *   - Simulates a new flow where the project starts empty and is built
 *     collaboratively with an AI agent (setup wizard)
 *
 * When DISABLED (false):
 *   - Shows the full template selection UI as normal
 *   - User can pick any available template
 */
export const USE_SETUP_WIZARD_FLOW = false;

/**
 * USE_SDK_AGENT_CHAT
 *
 * When ENABLED (true):
 *   - Allows the legacy SDK agent chat panel infrastructure in the editor layout
 *   - Actual visibility also requires USE_AGENT_NOVA_DEV or USE_AI_ASSISTANT_GATEWAY
 *
 * When DISABLED (false):
 *   - Disables SDK agent chat UI entry points in the editor
 */
export const USE_SDK_AGENT_CHAT = true;

/**
 * USE_AGENT_NOVA_DEV
 *
 * Compile-time gate for the legacy Agent Nova (SDK chat) panel.
 * Replaces the former Editor Tools > Agent Nova (Dev) menu toggle.
 *
 * When ENABLED (true):
 *   - Agent Nova title bar button, hotkey, and docked panel are available
 *     (when USE_SDK_AGENT_CHAT is also enabled and App Settings allow AI assistant)
 *
 * When DISABLED (false):
 *   - Agent Nova UI is hidden unless USE_AI_ASSISTANT_GATEWAY is enabled
 */
export const USE_AGENT_NOVA_DEV = false;

/**
 * USE_AI_ASSISTANT_GATEWAY
 *
 * Controlled by `NEXT_PUBLIC_DEV_ENABLE_AI_ASSISTANT` in packages/editor/.env.local
 * (must be the string `true`; unset or any other value is off). Requires dev server restart.
 *
 * When ENABLED (true):
 *   - Shows the Genesys AI Assistant panel (Gateway + Cursor CLI) instead of Agent Nova
 *
 * When DISABLED (false):
 *   - Uses the legacy Agent Nova panel when USE_SDK_AGENT_CHAT is enabled
 */
export const USE_AI_ASSISTANT_GATEWAY = process.env.NEXT_PUBLIC_DEV_ENABLE_AI_ASSISTANT === 'true';

/** Default when `app.aiAssistantEnabled` is not stored in Electron app state. */
export function getDefaultAiAssistantEnabled(): boolean {
  return USE_AI_ASSISTANT_GATEWAY;
}

/** Whether the docked SDK agent chat column should be available in the editor layout. */
export function isSdkAgentChatFeatureEnabled(): boolean {
  return USE_AI_ASSISTANT_GATEWAY || (USE_SDK_AGENT_CHAT && USE_AGENT_NOVA_DEV);
}
